<template>
    <div class="flex flex-col min-h-full">
        <DocumentShowHeader />

        <div
            id="document-pdf-container"
            class="grow flex flex-col"
        >
            <TabsPanel
                no-url
                model-value="document"
                panel-list-class="bg-white"
                class="flex flex-col grow"
                :hide-tabs="
                    !pdfViewerInitialized || !summaryAvailable || (!document.number_of_pages && viewerTotalPages === 0)
                "
                @update:model-value="handleTabSwitch"
            >
                <TabsPanelTab
                    id="document"
                    :name="t('document_one')"
                    class="document flex flex-col grow"
                    test-id="document-tab"
                >
                    <component
                        :is="tabs.DocumentShowDocument"
                        @set-viewer-total-pages="handleSetViewerTotalPages"
                    />
                </TabsPanelTab>
                <TabsPanelTab
                    id="summary"
                    icon="ai"
                    :name="t('summary')"
                    :lock="shouldLockAiContentTabs"
                    class="summary grow flex flex-col"
                    icon-test-id="ai-logo"
                    test-id="summary-tab"
                >
                    <component
                        :is="tabs.DocumentAiTab"
                        context="summary"
                        :viewer-total-pages="viewerTotalPages"
                    />
                </TabsPanelTab>
                <TabsPanelTab
                    id="flashcards"
                    icon="ai"
                    :name="t('flashcard_other')"
                    :lock="shouldLockAiContentTabs"
                    class="flashcards grow flex flex-col"
                    icon-test-id="ai-logo"
                    test-id="flashcards-tab"
                >
                    <component
                        :is="tabs.DocumentAiTab"
                        context="flashcards"
                        :viewer-total-pages="viewerTotalPages"
                    />
                </TabsPanelTab>
            </TabsPanel>

            <Transition name="fade">
                <XandrFullScreenAdWrapper
                    v-if="isLoggedIn && (isXandrAdLibLoaded || hasAdBlocker)"
                    :xandr-wrapper="`${xandrFullScreenType}Wrapper`"
                    :xandr-slot="xandrFullScreenType"
                    class="fixed"
                    is-full-screen-ad
                    @xandr-fullscreen-ad-viewed="storeFullScreenCookie"
                />
            </Transition>
        </div>

        <SurveyContainer
            v-if="showSurvey"
            :context-id="document.id"
            context-type="sd_document"
        ></SurveyContainer>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { downloadTimed } from '@/api/backend/document';
import { trackClick, trackImpression } from '@/modules/tracking';
import TabsPanel, { lazyLoadTab } from '@components/TabsPanel';
import TabsPanelTab from '@components/TabsPanelTab';
import GtmMixin, { GtmEventName } from '@/mixins/gtmMixin';
import SurveyContainer from '@components/SurveyContainer';
import { XandrFullScreenAdWrapper } from '@components/Xandr';
import { storeXandrFullScreenAdViewed } from '@/utils/cookieUtils';
import { EventBus, EventBusEvent } from '@plugins/eventBus.js';
import { useTranslation } from 'i18next-vue';
import DocumentShowHeader from './DocumentShowHeader.vue';

const AI_SUPPORTED_EXTENSIONS = ['pdf', 'doc', 'docx'];
const DOCUMENT_TAB = 'document';
const SUMMARY_TAB = 'summary';
const FLASHCARDS_TAB = 'flashcards';

export default {
    name: 'DocumentShow',
    components: {
        XandrFullScreenAdWrapper,
        DocumentShowHeader,
        TabsPanel,
        TabsPanelTab,
        SurveyContainer,
    },
    extends: GtmMixin,
    setup() {
        const { t } = useTranslation('document');
        return { t };
    },
    data() {
        return {
            countingVisitTime: false,
            summaryAvailable: false,
            showSurvey: false,
            viewerTotalPages: 0,
        };
    },
    computed: {
        ...mapGetters({
            pdfViewerInitialized: 'document/pdfViewerInitialized',
            document: 'document/currentDocument',
            isLoggedIn: 'auth/isLoggedIn',
            screen: 'ui/screen',
            user: 'auth/user',
            isBottomFixedAdAllowed: 'ad/isBottomFixedAdAllowed',
            hasAdBlocker: 'ad/hasAdBlocker',
            isXandrAdLibLoaded: 'xandr/isXandrAdLibLoaded',
        }),
        tabs() {
            return {
                DocumentShowDocument: lazyLoadTab(() => import('./DocumentShowDocument.vue')),
                DocumentAiTab: lazyLoadTab(() => import('./DocumentAiTab.vue')),
            };
        },
        shouldLockAiContentTabs() {
            return !this.isLoggedIn;
        },
        xandrFullScreenType() {
            return this.screen.minMD ? 'fullpage_video' : 'fullpage_video_mew';
        },
    },
    watch: {
        'document.summary_code': {
            handler() {
                if (this.document.summary_code && [200, 202, 204].includes(this.document.summary_code)) {
                    this.summaryAvailable = true;
                }
            },
        },
        pdfViewerInitialized(newVal) {
            if (newVal) {
                this.onPdfPagesLoaded();
            }
        },
    },
    created() {
        if (!this.isLoggedIn && this.document?.course?.university) {
            this.$store.dispatch('university/setCurrentUniversity', this.document.course.university);
        }
        if (
            this.isLoggedIn &&
            (this.document.has_ai_content || AI_SUPPORTED_EXTENSIONS.includes(this.document.extension))
        ) {
            this.$store.dispatch('document/getSummary');
        }
    },
    mounted() {
        EventBus.on(EventBusEvent.DOWNLOAD_DOCUMENT, this.onDocumentDownload);
        document.addEventListener('keydown', this.handleSaveEvent, { capture: true });
        // for xandr
        this.$store.dispatch('xandr/setXandrDocsViewedCookie', 1);

        if (window.sdWindow?.shouldTrackDocument) {
            trackImpression('DocumentPage', {
                file_id: this.document.id,
                referral_id: this.getReferralIdParameter(),
            });
        }
    },
    beforeUnmount() {
        EventBus.off(EventBusEvent.DOWNLOAD_DOCUMENT, this.onDocumentDownload);
        document.removeEventListener('keydown', this.handleSaveEvent);
    },
    methods: {
        getReferralIdParameter() {
            const urlParams = new URLSearchParams(window.location.search);
            return urlParams.get('ref');
        },
        handleSaveEvent(e) {
            const sKeyCode = 83;
            if ((e.metaKey || e.ctrlKey) && e.keyCode === sKeyCode) {
                e.stopPropagation();
                e.preventDefault();
            }
        },
        onPdfPagesLoaded() {
            if (window.sdWindow?.shouldTrackDocument) {
                if (this.countingVisitTime === false) {
                    this.countingVisitTime = true;

                    setTimeout(() => {
                        // no file is retrieved (only counts the download)
                        downloadTimed(this.document.id);
                        this.pushGtmEvent(GtmEventName.VIEW_DOCUMENT);
                        this.onDocumentDownload(false, true);
                    }, 30000);
                }
            }
        },
        onDocumentDownload(isDownloadButtonSticky = false, isTimedDownload = false) {
            const trackType = isDownloadButtonSticky ? 'DocumentDownloadSticky' : 'DocumentDownload';
            this.pushGtmEvent(GtmEventName.DOWNLOAD_DOCUMENT);
            trackClick(trackType, {
                file_id: this.document.id,
                is_timed_download: isTimedDownload,
            });
        },
        handleTabSwitch(tabId) {
            const aiContentTabs = [SUMMARY_TAB, FLASHCARDS_TAB];
            if (!aiContentTabs.includes(tabId)) {
                return;
            }
            if (this.shouldLockAiContentTabs) {
                this.$store.dispatch('ui/openLogin');
                return;
            }
            if (this.document.has_ai_content && this.showSurvey === false && this.user.is_premium) {
                this.showSurvey = true;
            }
        },
        tabIdIsValid(tabId) {
            const availableTabs = [DOCUMENT_TAB, SUMMARY_TAB, FLASHCARDS_TAB];
            return availableTabs.includes(tabId);
        },
        async submitRate(rate) {
            await this.$store.dispatch('document/setRate', rate);
        },
        storeFullScreenCookie(val) {
            storeXandrFullScreenAdViewed(val);
        },
        handleSetViewerTotalPages(totalPages) {
            this.viewerTotalPages = totalPages;
        },
    },
};
</script>

<style lang="scss" scoped>
:deep(.tab-panel__container) {
    @apply container;
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 1s;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
</style>
